import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { ArrowLeft, Lock, CreditCard, CheckCircle, Loader2, ShieldCheck, Tag, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
  });

  const subtotal = getCartTotal();
  const shipping = 5.99;
  const discountAmount = appliedDiscount ? appliedDiscount.discount_amount : 0;
  const total = subtotal + shipping - discountAmount;

  // Check if returning from Stripe with session_id
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const orderIdParam = searchParams.get('order_id');
    
    if (sessionId) {
      setCheckingPayment(true);
      setOrderId(orderIdParam || '');
      pollPaymentStatus(sessionId, 0);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setPaymentStatus('paid');
        setOrderId(data.order_id);
        setOrderComplete(true);
        setCheckingPayment(false);
        clearCart();
        toast.success('Payment successful!');
        return;
      } else if (data.status === 'expired' || data.status === 'canceled') {
        setCheckingPayment(false);
        toast.error('Payment was not completed. Please try again.');
        navigate('/checkout');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setCheckingPayment(false);
        toast.error('Error verifying payment. Please contact support.');
      }
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const response = await axios.post(`${API}/discounts/validate`, {
        code: promoCode.trim(),
        subtotal,
        items: cartItems.map(item => ({
          product_id: item.id,
          qty: item.quantity,
          unit_price: item.price,
        })),
        customer_email: formData.email || null,
      });
      setAppliedDiscount(response.data);
      toast.success(response.data.message);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Invalid promo code';
      toast.error(msg);
      setAppliedDiscount(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedDiscount(null);
    setPromoCode('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    const required = ['first_name', 'last_name', 'email', 'phone', 'address_line1', 'city', 'state', 'zip_code'];
    for (const field of required) {
      if (!formData[field]) {
        toast.error(`Please fill in ${field.replace('_', ' ')}`);
        return;
      }
    }

    setLoading(true);
    try {
      // Step 1: Create the order
      const orderData = {
        items: cartItems.map(item => ({
          product_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
        })),
        shipping: formData,
        subtotal,
        shipping_cost: shipping,
        discount: appliedDiscount ? {
          code: appliedDiscount.code,
          discount_type: appliedDiscount.discount_type,
          discount_value: appliedDiscount.discount_value,
          discount_amount: appliedDiscount.discount_amount,
        } : null,
        total,
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData);
      const newOrderId = orderResponse.data.order_id || orderResponse.data.id;
      setOrderId(newOrderId);

      // Step 2: Create Stripe checkout session
      const originUrl = window.location.origin;
      const checkoutResponse = await axios.post(`${API}/payments/checkout`, {
        order_id: newOrderId,
        origin_url: originUrl
      });

      // Step 3: Redirect to Stripe
      if (checkoutResponse.data.checkout_url) {
        window.location.href = checkoutResponse.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      const message = error.response?.data?.detail || 'Checkout failed. Please try again.';
      toast.error(message);
      setLoading(false);
    }
  };

  // Show payment verification screen
  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 border border-[#E5E0D8] text-center">
          <Loader2 className="w-16 h-16 text-[#D4540A] mx-auto mb-6 animate-spin" />
          <h1 className="font-['Bodoni_Moda'] text-2xl font-bold text-[#2A2A2A] mb-2">
            Verifying Payment...
          </h1>
          <p className="text-[#666]">
            Please wait while we confirm your payment.
          </p>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0 && !orderComplete) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex flex-col items-center justify-center px-4">
        <h1 className="font-['Bodoni_Moda'] text-3xl font-bold text-[#2A2A2A] mb-4">
          Your cart is empty
        </h1>
        <Link to="/shop">
          <Button className="bg-[#D4540A] hover:bg-[#B84709] text-white">
            Shop the Blends
          </Button>
        </Link>
      </div>
    );
  }

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4" data-testid="order-complete">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 border border-[#E5E0D8] text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-['Bodoni_Moda'] text-3xl font-bold text-[#2A2A2A] mb-2">
            Order Confirmed!
          </h1>
          <p className="text-[#666] mb-6">
            Thank you for your order. We'll get your blends shipped out soon.
          </p>
          <div className="bg-[#F5F3F0] rounded-lg p-4 mb-6">
            <p className="text-sm text-[#666]">Order ID</p>
            <p className="font-mono font-bold text-[#2A2A2A]" data-testid="order-id">{orderId}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center mb-2">
              <ShieldCheck className="w-5 h-5 text-green-600 mr-2" />
              <span className="font-semibold text-green-800">Payment Confirmed</span>
            </div>
            <p className="text-sm text-green-700">
              Your payment has been processed successfully. You'll receive a confirmation email shortly.
            </p>
          </div>
          <Link to="/shop">
            <Button className="w-full bg-[#D4540A] hover:bg-[#B84709] text-white py-6" data-testid="continue-btn">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Back Link */}
        <Link
          to="/cart"
          className="inline-flex items-center text-sm text-[#666] hover:text-[#2A2A2A] transition-colors mb-6"
          data-testid="back-to-cart"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Cart
        </Link>

        <h1 className="font-['Bodoni_Moda'] text-3xl sm:text-4xl font-bold text-[#2A2A2A] mb-8">
          Checkout
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Shipping Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Contact Info */}
              <div className="bg-white rounded-xl p-6 border border-[#E5E0D8]">
                <h2 className="font-semibold text-lg text-[#2A2A2A] mb-4">Contact Information</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      className="mt-1"
                      data-testid="input-first-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      className="mt-1"
                      data-testid="input-last-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="mt-1"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-1"
                      data-testid="input-phone"
                    />
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white rounded-xl p-6 border border-[#E5E0D8]">
                <h2 className="font-semibold text-lg text-[#2A2A2A] mb-4">Shipping Address</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="address_line1">Address Line 1 *</Label>
                    <Input
                      id="address_line1"
                      name="address_line1"
                      value={formData.address_line1}
                      onChange={handleChange}
                      className="mt-1"
                      data-testid="input-address1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address_line2">Address Line 2</Label>
                    <Input
                      id="address_line2"
                      name="address_line2"
                      value={formData.address_line2}
                      onChange={handleChange}
                      className="mt-1"
                      placeholder="Apt, Suite, Unit (optional)"
                      data-testid="input-address2"
                    />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="mt-1"
                        data-testid="input-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className="mt-1"
                        data-testid="input-state"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zip_code">ZIP Code *</Label>
                      <Input
                        id="zip_code"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleChange}
                        className="mt-1"
                        data-testid="input-zip"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="mt-1"
                      data-testid="input-country"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Section */}
              <div className="bg-white rounded-xl p-6 border border-[#E5E0D8]">
                <h2 className="font-semibold text-lg text-[#2A2A2A] mb-4 flex items-center">
                  <Lock className="w-5 h-5 mr-2 text-[#666]" />
                  Payment
                </h2>
                <div className="bg-[#F5F3F0] border border-[#E5E0D8] rounded-lg p-6">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <CreditCard className="w-8 h-8 text-[#D4540A]" />
                    <ShieldCheck className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-[#2A2A2A] text-center mb-2">
                    Secure Stripe Checkout
                  </h3>
                  <p className="text-sm text-[#666] text-center">
                    You'll be redirected to Stripe's secure payment page after clicking "Proceed to Payment".
                  </p>
                  <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-[#E5E0D8]">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" className="h-5 opacity-50" />
                    <img src="https://upload.wikimedia.org/wikipedia/commons/0/04/Visa.svg" alt="Visa" className="h-5" />
                    <img src="https://upload.wikimedia.org/wikipedia/commons/a/a4/Mastercard_2019_logo.svg" alt="Mastercard" className="h-5" />
                    <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/American_Express_logo_%282018%29.svg" alt="Amex" className="h-5" />
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl p-6 border border-[#E5E0D8] sticky top-24" data-testid="checkout-summary">
                <h2 className="font-['Bodoni_Moda'] text-xl font-bold text-[#2A2A2A] mb-6">
                  Order Summary
                </h2>

                {/* Items */}
                <div className="space-y-4 mb-6">
                  {cartItems.map((item) => {
                    const isBundle = item.is_bundle || item.id === 'full-set';
                    return (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="text-[#666]">
                          {item.name} {isBundle && <span className="text-xs">(4-pack)</span>} × {item.quantity}
                        </span>
                        <span className="font-medium text-[#2A2A2A]">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-[#E5E0D8] pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#666]">Subtotal</span>
                    <span className="font-medium text-[#2A2A2A]">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#666]">Shipping</span>
                    <span className="font-medium text-[#2A2A2A]">${shipping.toFixed(2)}</span>
                  </div>

                  {/* Promo Code Input */}
                  {!appliedDiscount ? (
                    <div className="pt-2">
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#999]" />
                          <Input
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                            placeholder="Promo code"
                            className="pl-9 h-9 text-sm font-mono"
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleApplyPromo())}
                            data-testid="promo-code-input"
                          />
                        </div>
                        <Button
                          type="button"
                          onClick={handleApplyPromo}
                          disabled={promoLoading || !promoCode.trim()}
                          variant="outline"
                          size="sm"
                          className="h-9 px-4 text-sm border-[#D4540A] text-[#D4540A] hover:bg-[#D4540A] hover:text-white"
                          data-testid="apply-promo-btn"
                        >
                          {promoLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2" data-testid="applied-discount">
                      <div>
                        <span className="text-green-800 text-sm font-semibold font-mono">{appliedDiscount.code}</span>
                        <span className="text-green-700 text-xs ml-2">
                          {appliedDiscount.discount_type === 'percentage' ? `${appliedDiscount.discount_value}% off` : `$${appliedDiscount.discount_value} off`}
                        </span>
                      </div>
                      <button onClick={handleRemovePromo} className="text-green-600 hover:text-green-800 p-1" data-testid="remove-promo-btn">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Discount line item */}
                  {appliedDiscount && (
                    <div className="flex justify-between text-sm text-green-700">
                      <span>Discount ({appliedDiscount.code})</span>
                      <span className="font-medium">-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}

                  <div className="border-t border-[#E5E0D8] pt-3">
                    <div className="flex justify-between">
                      <span className="font-semibold text-[#2A2A2A]">Total</span>
                      <span className="font-bold text-xl text-[#2A2A2A]" data-testid="checkout-total">
                        ${total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full mt-6 bg-[#D4540A] hover:bg-[#B84709] text-white py-6 text-lg font-semibold"
                  data-testid="place-order-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </span>
                  ) : (
                    'Proceed to Payment'
                  )}
                </Button>

                <p className="text-xs text-center text-[#999] mt-4">
                  By placing this order, you agree to our Terms of Service and Privacy Policy.
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutPage;
